import {
  Banner,
  Container,
  Header,
  Layout,
  MainContent,
  Section,
} from '@inmotionnow/momentum-components-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { localeIdToValue, userTypeIdToValue } from '@/features/users/user-utils'
import {
  useCreateUserPublicMutation,
  useGetCurrentUserQuery,
} from '@/features/users/userApi'
import {
  UserCreateForm,
  UserCreateFormValues,
} from '@/features/users/UserCreateForm'
import {
  getBackendErrorMessage,
  isEmailConflictError,
  isForbiddenError,
  isInsufficientManagePermissionError,
  isPermissionCheckUnavailableError,
  isSeatLimitError,
} from '@/features/users/userErrors'

import { BulkImportPanel } from './bulk-import-panel'
import { CreateUserTab, CreateUserTabs } from './create-user-tabs'

/**
 * Public "Invite User" view at /user/create. Wraps the shared UserCreateForm in
 * the Invite/Import tab shell, resolves the target account from the ?accountId=
 * query param (numeric platform account id), submits through the public endpoint,
 * and navigates to the new user's detail page on success. Sharing the form with
 * the standalone + embedded variants keeps its fields, labels, and validation
 * from drifting (KPS-448).
 */
const UserPublicCreateView = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [createUserPublic, { isLoading: isCreating }] =
    useCreateUserPublicMutation()
  // Bulk import is an admin action. The backend refuses a team member either way —
  // this keeps the tab from advertising something they cannot do (as KPS-442 does for
  // the User Type field).
  const { data: currentUser } = useGetCurrentUserQuery()
  const canBulkImport = currentUser?.user_type === 'admin'

  const [emailError, setEmailError] = React.useState<string | undefined>(
    undefined,
  )

  const resolvedAccountId = React.useMemo(() => {
    const paramId = searchParams.get('accountId')
    if (!paramId || !/^\d+$/.test(paramId)) return null
    const parsed = parseInt(paramId, 10)
    return parsed > 0 ? parsed : null
  }, [searchParams])

  const handleSubmit = async (values: UserCreateFormValues): Promise<void> => {
    if (!resolvedAccountId) return

    const userType = userTypeIdToValue(values.userTypeId!)
    if (!userType) return

    try {
      const result = await createUserPublic({
        account_id: resolvedAccountId,
        user: {
          email: values.email.trim(),
          first_name: values.firstName.trim(),
          last_name: values.lastName.trim(),
          title: values.title.trim() || undefined,
          phone: values.phone.trim() || undefined,
          locale: localeIdToValue(values.locale),
          timezone:
            values.timezone.trim() ||
            Intl.DateTimeFormat().resolvedOptions().timeZone,
          user_type: userType,
        },
      }).unwrap()

      toast.success(t('USER_CREATED_SUCCESSFULLY'))
      navigate(`/user/details/${result.id}?accountId=${resolvedAccountId}`)
    } catch (error) {
      if (isEmailConflictError(error)) {
        setEmailError(t('EMAIL_ALREADY_IN_USE'))
      } else if (isInsufficientManagePermissionError(error)) {
        // Checked before isForbiddenError, which also matches a 403 but can only
        // offer the generic message (KPS-444).
        toast.error(t('INSUFFICIENT_MANAGE_PERMISSION'))
      } else if (isForbiddenError(error)) {
        toast.error(t('PERMISSION_DENIED_CREATING_USER'))
      } else if (isPermissionCheckUnavailableError(error)) {
        toast.error(t('PERMISSION_CHECK_UNAVAILABLE'))
      } else if (isSeatLimitError(error)) {
        toast.error(t('NO_TEAM_MEMBER_SEATS_AVAILABLE'))
      } else {
        // Prefer the backend's own explanation of a 4xx over the generic string,
        // so the user learns why the create was rejected (KPS-513).
        toast.error(getBackendErrorMessage(error) ?? t('ERROR_CREATING_USER'))
      }
    }
  }

  if (resolvedAccountId === null) {
    return (
      <Layout>
        <Header>
          <Banner title={t('CREATE_USER')} />
        </Header>
        <MainContent>
          <Section>
            <Container size='sm'>{t('NO_ACCOUNT_CONTEXT')}</Container>
          </Section>
        </MainContent>
      </Layout>
    )
  }

  const tabs: CreateUserTab[] = [
    {
      id: 'create-user-invite',
      label: t('INVITE'),
      panel: (
        <UserCreateForm
          formAriaLabel={t('CREATE_USER')}
          isSubmitting={isCreating}
          emailError={emailError}
          onEmailChange={() => setEmailError(undefined)}
          onSubmit={handleSubmit}
          onCancel={() => navigate(-1)}
        />
      ),
    },
  ]

  if (canBulkImport) {
    tabs.push({
      id: 'create-user-import',
      label: t('IMPORT'),
      panel: <BulkImportPanel accountId={resolvedAccountId} />,
    })
  }

  return (
    <Layout>
      <Header>
        <Banner title={t('CREATE_USER')} />
      </Header>
      <MainContent>
        <Section>
          <Container size='sm'>
            <CreateUserTabs tabs={tabs} ariaLabel={t('CREATE_USER')} />
          </Container>
        </Section>
      </MainContent>
    </Layout>
  )
}

export { UserPublicCreateView }
